// Pure decision logic for the contextual button labels + payloads.

import type { ActionBody, CardView, Pending, PlayerId } from "../api/types";
import type { Selection } from "./selection";

export interface PrimaryAction {
  /** Button label, e.g. "Play 3 mana". */
  label: string;
  /** `true` when we should render the button disabled (no-op). */
  disabled: boolean;
  hint?: string;
  /** POST /action payload. Null when disabled. */
  action: ActionBody | null;
  /** "confirm" → faction-accent color (phase-resolving actions). */
  emphasis: "play" | "confirm" | "none";
}

export interface EndTurnAction {
  label: string;
  disabled: boolean;
  action: ActionBody | null;
}

export interface ActionContext {
  pending: Pending | null;
  selection: Selection;
  mana: number;
  handSize: number;
  /** Re-indexed each play; primaryAction verifies inspected hand_idx
   *  still points at the same card. */
  hand: CardView[];
  animating: boolean;
  gameOver: boolean;
  /** Drives the main-phase button label ("Attack Phase" vs "End Turn"). */
  hasAttackableCreatures: boolean;
  /** Returns null when pending belongs to the other seat (PvP gate). */
  viewer: PlayerId;
}

function pendingBelongsToViewer(ctx: ActionContext): boolean {
  return ctx.pending != null && ctx.pending.player === ctx.viewer;
}

export function primaryAction(
  ctx: ActionContext,
  inspected: CardView | null,
  inspectedHandIdx: number | null,
): PrimaryAction | null {
  const { pending, gameOver } = ctx;
  if (gameOver) return null;
  if (!pending) return null;
  if (!pendingBelongsToViewer(ctx)) return null;

  // Verify inspected hand_idx still points at the same card; after a
  // play the hand re-indexes.
  const stillInHand =
    inspected != null &&
    inspectedHandIdx != null &&
    ctx.hand[inspectedHandIdx]?.entity_id === inspected.entity_id;

  // `animating` deliberately does NOT gate the button. `submitAction`
  // runs `cancelAnimation` before doing anything, which snaps the
  // BattleState forward and aborts the running animation, so a click
  // during the pacing tail fires the action instead of being dropped.

  if (pending.kind === "main") {
    if (inspected == null || inspectedHandIdx == null) return null;
    if (!stillInHand) return null;
    const canAfford = inspected.cost <= ctx.mana;
    return {
      label: canAfford ? `Play ${inspected.cost} mana` : `Need ${inspected.cost - ctx.mana} more mana`,
      disabled: !canAfford,
      action: canAfford ? { kind: "play", hand_idx: inspectedHandIdx } : null,
      emphasis: "play",
    };
  }

  if (pending.kind === "discard") {
    if (inspected == null || inspectedHandIdx == null) return null;
    if (!stillInHand) return null;
    return {
      label: `Discard ${inspected.name}`,
      disabled: false,
      action: { kind: "discard", hand_idx: inspectedHandIdx },
      emphasis: "confirm",
    };
  }

  return null;
}

export function endTurnAction(ctx: ActionContext): EndTurnAction | null {
  const { pending, gameOver, selection } = ctx;
  if (gameOver) return null;
  if (!pending) return null;
  if (!pendingBelongsToViewer(ctx)) return null;
  // `animating` deliberately does NOT gate the button — see
  // `primaryAction` for the cancel-on-input design that makes this
  // safe.

  if (pending.kind === "main") {
    return {
      label: ctx.hasAttackableCreatures ? "Attack Phase" : "End Turn",
      disabled: false,
      action: { kind: "end_main" },
    };
  }
  if (pending.kind === "attackers") {
    const n = selection.attackers.size;
    return {
      label: n > 0 ? `Attack (${n})` : "No Attack",
      disabled: false,
      action: { kind: "attackers", attackers: Array.from(selection.attackers) },
    };
  }
  if (pending.kind === "blocks") {
    let total = 0;
    for (const s of selection.blocks.values()) total += s.size;
    return {
      label: total > 0 ? `Confirm Blocks (${total})` : "No Blocks",
      disabled: false,
      action: {
        kind: "blocks",
        blocks: Array.from(selection.blocks.entries())
          .filter(([, s]) => s.size > 0)
          .map(([a, s]) => ({ attacker: a, blockers: Array.from(s) })),
      },
    };
  }
  if (pending.kind === "discard") {
    const inspected = selection.inspected;
    const idx = selection.handIdx;
    const stillInHand =
      inspected != null &&
      idx != null &&
      ctx.hand[idx]?.entity_id === inspected.entity_id;
    if (stillInHand) {
      return {
        label: `Discard ${inspected.name}`,
        disabled: false,
        action: { kind: "discard", hand_idx: idx },
      };
    }
    return { label: "Discard a card", disabled: true, action: null };
  }
  return null;
}

/** The contextual bottom-right button is only a live decision once the
 *  paced event replay has caught up to where the engine is actually
 *  waiting for input. `liveTargetKey` is the turn-qualified `pendingKey`
 *  of the in-flight batch's final pending; `displayedKey` is the same for
 *  the currently-shown (possibly lagging) snapshot. While they disagree
 *  the button must stay inert — otherwise it offers a stale decision
 *  (e.g. our own "Attack (1)" lingering as combat resolves into the
 *  opponent's turn) that the engine would reject. When idle (not
 *  animating) the displayed snapshot is authoritative, so it's settled. */
export function actionButtonSettled(
  animating: boolean,
  liveTargetKey: string | null,
  displayedKey: string,
): boolean {
  if (!animating) return true;
  if (liveTargetKey == null) return true;
  return displayedKey === liveTargetKey;
}

/** Human-readable label of the current phase, for the center seam. */
export function phaseLabel(pending: Pending | null): string {
  if (!pending) return "Waiting…";
  switch (pending.kind) {
    case "main":
      return "Main";
    case "attackers":
      return "Declare Attackers";
    case "blocks":
      return "Declare Blockers";
    case "discard":
      return "Discard";
    case "target":
      return "Choose Targets";
    case "choice":
      return "Choose";
  }
}
